import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { useImageUrls } from '../../services/uploads';
import {
  usePrize,
  useDeletePrize,
  usePublishPrize,
  useUnpublishPrize,
  useClaims,
  useFulfillClaim,
  useCancelClaim,
} from '../../services/gamification';
import type { PrizeCategory, PrizeClaim } from '../../types/gamification';
import {
  GiftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { palette } from '@/styles/figma-tokens';

// ─── Constants ──────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<PrizeCategory, string> = {
  merchandise: 'Merchandise',
  gift_card: 'Gift Card',
  experience: 'Experience',
  digital: 'Digital',
  certificate: 'Certificate',
  custom: 'Custom',
};

const CLAIM_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-warning-50 text-warning-700 dark:bg-warning-950/40 dark:text-warning-300',
  fulfilled: 'bg-success-50 text-success-700 dark:bg-success-950/40 dark:text-success-300',
  cancelled: 'bg-grey-50 text-grey-500 dark:bg-grey-800/50 dark:text-grey-400',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Shared typography style objects ────────────────────────────────────────

const fontPoppins = "'Poppins', sans-serif";

// ─── Main Component ─────────────────────────────────────────────────────────

export default function PrizeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const { data: prize, isLoading, error } = usePrize(id);
  const deleteMutation = useDeletePrize();
  const publishMutation = usePublishPrize();
  const unpublishMutation = useUnpublishPrize();
  const fulfillMutation = useFulfillClaim();
  const cancelMutation = useCancelClaim();

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [activeImage, setActiveImage] = useState(0);
  const [claimsPage, setClaimsPage] = useState(1);
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);

  const { data: claimsData } = useClaims(
    id ? { prize_id: id, page: claimsPage, per_page: 10 } : {}
  );

  // Resolve S3 keys to presigned URLs (must be called before any early returns)
  const rawMediaUrls = prize?.media_urls ?? [];
  const mediaUrls = useImageUrls(rawMediaUrls);
  const hasMedia = mediaUrls.length > 0;

  // Theme-aware palette — this view was authored light-only; the hero gradient,
  // surfaces and text must all read in both light and dark.
  const isDark = theme === 'dark';
  const textPrimary = 'rgb(var(--color-text-primary))';
  const textSecondary = 'rgb(var(--color-text-secondary))';
  const primaryMain = 'rgb(var(--color-primary))';
  const heroGradient = isDark
    ? 'linear-gradient(135deg, #3a2358 0%, #4a2f6e 30%, #5a2f5a 60%, #6b4a2e 100%)'
    : 'linear-gradient(135deg, #6B3FA0 0%, rgb(var(--color-primary)) 30%, #C060A1 60%, #E8763A 100%)';
  const heroOverlay = isDark ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.3)';
  const heroBorder = isDark ? 'rgba(255,255,255,0.12)' : 'white';
  const controlBorder = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.15)';
  const typo = {
    h5: { fontFamily: fontPoppins, fontWeight: 700, lineHeight: '30px', color: textPrimary, fontSize: '20px', letterSpacing: '0px' },
    h6: { fontFamily: fontPoppins, fontWeight: 600, lineHeight: '28px', color: textPrimary, fontSize: '18px', letterSpacing: '0px' },
    h6Purple: { fontFamily: fontPoppins, fontWeight: 600, lineHeight: '28px', color: 'rgb(var(--color-primary-400))', fontSize: '18px', letterSpacing: '0px' },
    subtitle2: { fontFamily: fontPoppins, fontWeight: 600, lineHeight: '22px', color: textPrimary, fontSize: '14px', letterSpacing: '0px' },
    body2: { fontFamily: fontPoppins, lineHeight: '22px', color: textPrimary, fontSize: '14px', letterSpacing: '0px' },
    body2Secondary: { fontFamily: fontPoppins, lineHeight: '22px', color: textSecondary, fontSize: '14px', letterSpacing: '0px' },
    body2Purple: { fontFamily: fontPoppins, lineHeight: '22px', color: primaryMain, fontSize: '14px', letterSpacing: '0px' },
    caption: { fontFamily: fontPoppins, lineHeight: '18px', color: textSecondary, fontSize: '12px', letterSpacing: '0px' },
    captionPrimary: { fontFamily: fontPoppins, lineHeight: '18px', color: textPrimary, fontSize: '12px', letterSpacing: '0px' },
    buttonMedium: { fontFamily: fontPoppins, fontWeight: 700, lineHeight: '24px', color: textPrimary, fontSize: '14px' },
    buttonMediumWhite: { fontFamily: fontPoppins, fontWeight: 700, lineHeight: '24px', color: 'white', fontSize: '14px' },
  };

  // Set defaults when prize loads
  React.useEffect(() => {
    if (prize) {
      if (prize.colors.length > 0 && selectedColor === null) {
        setSelectedColor(prize.colors[0]);
      }
      if (prize.sizes.length > 0 && selectedSize === '') {
        setSelectedSize(prize.sizes[0]);
      }
    }
  }, [prize, selectedColor, selectedSize]);

  const handleTogglePublish = async () => {
    if (!prize) return;
    setPublishMenuOpen(false);
    try {
      if (prize.is_draft) {
        await publishMutation.mutateAsync(prize.id);
        toast.success('Prize published');
      } else {
        await unpublishMutation.mutateAsync(prize.id);
        toast.success('Prize unpublished');
      }
    } catch {
      toast.error('Failed to update prize status');
    }
  };

  const handleDelete = async () => {
    if (!prize || !window.confirm(`Delete "${prize.title}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync(prize.id);
      toast.success('Prize deleted');
      navigate('/prizes');
    } catch {
      toast.error('Failed to delete prize. It may have pending claims.');
    }
  };

  const handleFulfill = async (claim: PrizeClaim) => {
    try {
      await fulfillMutation.mutateAsync({ claimId: claim.id });
      toast.success('Claim approved');
    } catch {
      toast.error('Could not approve the claim');
    }
  };

  const handleCancelClaim = async (claim: PrizeClaim) => {
    if (!window.confirm('Cancel this claim and refund points?')) return;
    try {
      await cancelMutation.mutateAsync({ claimId: claim.id });
      toast.success('Claim rejected — points refunded');
    } catch {
      toast.error('Could not reject the claim');
    }
  };

  const goToPrev = () =>
    setActiveImage((prev) => (prize && prev > 0 ? prev - 1 : (prize?.media_urls.length ?? 1) - 1));
  const goToNext = () =>
    setActiveImage((prev) =>
      prize && prev < prize.media_urls.length - 1 ? prev + 1 : 0
    );

  // ── Error / not found state ──────────────────────────────────────────────
  if (!isLoading && (error || !prize)) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <GiftIcon className="w-12 h-12 text-text-tertiary/40" />
        <p className="text-sm text-text-secondary">Prize not found.</p>
        <button
          onClick={() => navigate('/prizes')}
          className="text-sm font-medium text-primary-600 dark:text-primary-350 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
        >
          Back to Prizes
        </button>
      </div>
    );
  }

  const claims = claimsData?.claims ?? [];
  const claimsPagination = claimsData?.pagination;
  const isPublishPending = publishMutation.isPending || unpublishMutation.isPending;

  // Fallback prize for phantom-ui to measure during loading
  const p = prize ?? {
    id: '', title: 'Prize title', description: 'Prize description placeholder',
    content: null, category: 'merchandise' as PrizeCategory, price_points: 0,
    retail_amount: null, retail_currency: 'USD', set_quantity: 0, available_quantity: 0,
    gender: 'unisex', sku: null, internal_code: null, is_draft: true,
    colors: [] as string[], sizes: [] as string[], tags: [] as string[],
    media_urls: [] as string[], created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  return (
    <motion.div {...slideUp} className="relative w-full pb-6">
      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>

      {/* Standard header position: entity name as the title, real path under
          it — same spot as the list page so the transition doesn't jump. */}
      <PageHeader
        title={p.title}
        className="mb-4"
        trail={[{ label: 'Prize Management', to: '/prizes' }]}
      />

      {/* ── Top action bar ─────────────────────────────────────────────────── */}
      <div
        className="content-stretch flex h-[36px] items-center justify-end w-full"
        style={{ gap: '12px' }}
      >

        {/* Back button */}
        <button
          onClick={() => navigate('/prizes')}
          className="content-stretch flex items-center justify-center min-h-[36px] min-w-[64px] relative shrink-0 hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors"
          style={{ gap: '8px', paddingLeft: '8px', paddingRight: '8px', paddingTop: '6px', paddingBottom: '6px', borderRadius: '8px' }}
        >
          {/* Back arrow icon */}
          <div className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center">
            <svg className="size-[20px]" style={{ color: textPrimary }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-center whitespace-nowrap" style={typo.buttonMedium}>
            <p style={{ lineHeight: '24px' }}>Back</p>
          </div>
        </button>

        {/* Spacer */}
        <div className="flex-[1_0_0] h-[20px] min-h-px min-w-px" />

        {/* Preview as learner — opens the learner-facing prize view in a new tab */}
        <button
          onClick={() => window.open(`/learn/prizes/${p.id}/claim`, '_blank', 'noopener,noreferrer')}
          disabled={!prize}
          className="content-stretch flex items-center justify-center relative shrink-0 hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ padding: '8px', borderRadius: '500px' }}
          title="Preview as learner"
          aria-label="Preview as learner"
        >
          <div className="content-stretch flex items-center justify-center relative shrink-0">
            <div className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center">
              <EyeIcon className="size-[20px]" style={{ color: textSecondary }} />
            </div>
          </div>
        </button>

        {/* Edit icon button */}
        <button
          onClick={() => navigate(`/prizes/${p.id}/edit`)}
          disabled={!prize}
          className="content-stretch flex items-center justify-center relative shrink-0 hover:bg-grey-100 dark:hover:bg-grey-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ padding: '8px', borderRadius: '500px' }}
          title="Edit prize"
          aria-label="Edit prize"
        >
          <div className="content-stretch flex items-center justify-center relative shrink-0">
            <div className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center">
              <svg className="size-[20px]" style={{ color: textSecondary }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343z" />
              </svg>
            </div>
          </div>
        </button>

        {/* Publish / Draft dropdown button */}
        <div className="relative shrink-0">
          <button
            onClick={() => setPublishMenuOpen((o) => !o)}
            disabled={isPublishPending}
            className="content-stretch flex items-center justify-center min-h-[36px] min-w-[64px] relative shrink-0 transition-colors disabled:opacity-60"
            style={{
              gap: '8px',
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: '6px',
              paddingBottom: '6px',
              borderRadius: '8px',
              backgroundColor: p.is_draft ? 'transparent' : primaryMain,
              color: p.is_draft ? textPrimary : 'white',
            }}
          >
            {isPublishPending && (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-center whitespace-nowrap"
              style={{ fontFamily: fontPoppins, fontWeight: 700, fontSize: '14px' }}
            >
              <p style={{ lineHeight: '24px' }}>
                {p.is_draft ? 'Draft' : 'Published'}
              </p>
            </div>
            {/* Chevron down icon */}
            <div className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center">
              <svg className="size-[20px]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
              </svg>
            </div>
          </button>

          {publishMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setPublishMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-grey-900 rounded-xl border border-border-primary shadow-lg py-1 min-w-[148px]">
                {p.is_draft ? (
                  <button
                    onClick={handleTogglePublish}
                    className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 transition-colors"
                  >
                    Publish
                  </button>
                ) : (
                  <button
                    onClick={handleTogglePublish}
                    className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 transition-colors"
                  >
                    Unpublish
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="w-full text-left px-4 py-2 text-sm text-error-600 hover:bg-error-50 dark:hover:bg-error-950/20 transition-colors disabled:opacity-50"
                >
                  Delete prize
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="content-stretch flex flex-col items-start w-full mt-6" style={{ gap: '50px' }}>

        {/* ── Hero card (ProductDetailsSummaryNew — light mode) ───────────── */}
        <div
          className="relative w-full overflow-hidden rounded-[16px] border border-solid"
          style={{ borderColor: heroBorder, boxShadow: '10px 12px 27px 0px rgba(0,0,0,0.09)' }}
        >

            {/* Background image + frosted overlay */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none rounded-[16px]">
              <div
                className="absolute inset-0 rounded-[16px]"
                style={{
                  background: heroGradient,
                }}
              />
              <div className="absolute inset-0 rounded-[16px]" style={{ backgroundColor: heroOverlay }} />
            </div>

            {/* Responsive two-column layout — centered so the buy-box stays compact */}
            <div className="relative flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 p-6 sm:p-8 lg:px-[53px] lg:py-[40px]">

              {/* Right content column */}
              <div className="content-stretch flex flex-col gap-[9px] items-start relative w-full max-w-[442px] min-w-0">

              {/* Title + description */}
              <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ gap: '16px' }}>
                <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ gap: '0px' }}>
                  <p className="not-italic relative shrink-0 w-full" style={typo.h5}>
                    {p.title}
                  </p>
                </div>
                {p.description && (
                  <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                    {p.description}
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="h-px relative shrink-0 w-full">
                <div className="-translate-y-1/2 absolute h-px left-0 right-0 top-1/2 bg-black/10 dark:bg-white/10" />
              </div>

              {/* Color row */}
              {p.colors.length > 0 && (
                <div className="content-stretch flex items-start relative shrink-0 w-full">
                  <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative" style={typo.subtitle2}>
                    Color
                  </p>
                  {/* Color swatches */}
                  <div className="content-stretch flex items-start relative shrink-0">
                    {p.colors.map((color) => {
                      const isSelected = color === selectedColor;
                      return (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          title={color}
                          className="content-stretch flex items-center justify-center relative shrink-0 size-[36px]"
                        >
                          <span
                            className="rounded-full shrink-0 transition-[width,height] duration-150"
                            style={{
                              backgroundColor: color,
                              width: isSelected ? '26px' : '18px',
                              height: isSelected ? '26px' : '18px',
                              border: isSelected ? '2px solid rgb(var(--color-primary) / 0.08)' : undefined,
                              boxShadow: isSelected ? '0px 8px 16px 0px rgb(var(--color-primary) / 0.24)' : undefined,
                            }}
                          />
                          {isSelected && (
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <svg className="size-[12px] text-white drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size row */}
              {p.sizes.length > 0 && (
                <div className="content-stretch flex items-start relative shrink-0 w-full">
                  <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative" style={typo.subtitle2}>
                    Size
                  </p>
                  <div className="content-stretch flex flex-col items-end relative shrink-0 w-[112px]">
                    {/* Select field */}
                    <div
                      className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderWidth: '0.8px', borderColor: controlBorder, paddingLeft: '14px', paddingRight: '14px', borderRadius: '8px' }}
                    >
                      <div
                        className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
                        style={{ paddingTop: '8px', paddingBottom: '8px' }}
                      >
                        <select
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                          className="w-full min-w-0 not-italic whitespace-nowrap appearance-none bg-transparent focus:outline-none cursor-pointer leading-[22px]"
                          style={{ fontFamily: fontPoppins, color: textPrimary, fontSize: '15px' }}
                        >
                          {p.sizes.map((size) => (
                            <option key={size} value={size} className="text-text-primary bg-white dark:bg-grey-900">
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Select arrow */}
                      <div
                        className="-translate-y-1/2 absolute content-stretch flex items-center top-1/2 pointer-events-none"
                        style={{ right: '-0.8px', paddingRight: '10px' }}
                      >
                        <div className="overflow-clip relative shrink-0 size-[18px] flex items-center justify-center">
                          <svg className="size-[14px]" style={{ color: textSecondary }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quantity row */}
              <div className="content-stretch flex items-start relative shrink-0 w-full">
                <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative" style={typo.subtitle2}>
                  Quantity
                </p>
                <div className="content-stretch flex flex-col items-end relative shrink-0" style={{ gap: '8px' }}>
                  {/* Stepper */}
                  <div
                    className="border border-solid content-stretch flex items-center justify-center relative rounded-[8px] shrink-0 w-[88px]"
                    style={{ borderWidth: '0.8px', borderColor: controlBorder, padding: '4px' }}
                  >
                    <div className="content-stretch flex items-center justify-center relative shrink-0 size-[26px]">
                      <button
                        disabled
                        className="overflow-clip relative shrink-0 size-[16px] flex items-center justify-center opacity-40 cursor-not-allowed"
                        aria-label="Decrease quantity"
                      >
                        <svg className="size-[14px]" style={{ color: textSecondary }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative text-center" style={typo.subtitle2}>
                      1
                    </p>
                    <div className="content-stretch flex items-center justify-center relative shrink-0 size-[26px]">
                      <button
                        disabled
                        className="overflow-clip relative shrink-0 size-[16px] flex items-center justify-center opacity-40 cursor-not-allowed"
                        aria-label="Increase quantity"
                      >
                        <svg className="size-[14px]" style={{ color: textSecondary }} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {/* Available count */}
                  <p className="not-italic relative shrink-0 text-right whitespace-nowrap" style={typo.captionPrimary}>
                    Available: {p.set_quantity === 0 ? 'Unlimited' : p.available_quantity}
                  </p>
                </div>
              </div>

              {/* Bottom divider */}
              <div className="h-px shrink-0 w-full bg-black/10 dark:bg-white/10" />
              </div>

              {/* Left: Product image + thumbnails */}
              <div className="order-first flex flex-col items-center gap-6 w-full max-w-[432px] min-w-0">

            {/* Main image */}
            <div className="relative w-full max-w-[432px] aspect-square mx-auto overflow-hidden rounded-[16px]">
              {hasMedia ? (
                <img
                  alt={p.title}
                  className="absolute inset-0 max-w-none object-contain pointer-events-none size-full"
                  src={mediaUrls[activeImage]}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-white/10">
                  <GiftIcon className="w-20 h-20 text-text-secondary/30" />
                </div>
              )}

              {/* Carousel pill — bottom-right corner of image */}
              {mediaUrls.length > 1 && (
                <div className="absolute bottom-3 right-3">
                  <div
                    className="flex items-center overflow-clip"
                    style={{ backgroundColor: palette.grey['800'], gap: '4px', padding: '4px', borderRadius: '8px' }}
                  >
                    {/* Prev */}
                    <button
                      onClick={goToPrev}
                      className="content-stretch flex items-center justify-center opacity-[0.48] relative shrink-0 hover:opacity-100 transition-opacity"
                      style={{ padding: '5px', borderRadius: '500px' }}
                      aria-label="Previous image"
                    >
                      <div className="overflow-clip relative shrink-0 size-[18px] flex items-center justify-center">
                        <svg className="size-[14px]" style={{ color: 'white' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </button>
                    {/* Counter */}
                    <p className="not-italic relative shrink-0 text-center whitespace-nowrap" style={{ ...typo.subtitle2, color: 'white' }}>
                      {activeImage + 1}/{mediaUrls.length}
                    </p>
                    {/* Next */}
                    <button
                      onClick={goToNext}
                      className="content-stretch flex items-center justify-center relative shrink-0 hover:opacity-80 transition-opacity"
                      style={{ padding: '5px', borderRadius: '500px' }}
                      aria-label="Next image"
                    >
                      <div className="overflow-clip relative shrink-0 size-[18px] flex items-center justify-center">
                        <svg className="size-[14px]" style={{ color: 'white' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {mediaUrls.length > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-3 w-full">
                {mediaUrls.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className="relative shrink-0 transition-[border-color,opacity] duration-150"
                    style={{
                      borderRadius: '12px',
                      border: idx === activeImage ? `2px solid ${primaryMain}` : undefined,
                      opacity: idx === activeImage ? 1 : 0.48,
                    }}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <div className="relative size-[64px] overflow-hidden" style={{ borderRadius: '12px' }}>
                      <img
                        alt=""
                        className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                        style={{ borderRadius: '12px' }}
                        src={url}
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
              </div>
            </div>
          </div>

        {/* ── Description / tabs card ────────────────────────────────────────── */}
        <div
          className="content-stretch flex flex-col items-start overflow-clip relative shrink-0 w-full bg-surface-elevated"
          style={{ borderRadius: '16px', boxShadow: '0px 0px 2px 0px rgba(189,188,201,0.2), 0px 12px 24px -4px rgba(189,188,201,0.12)' }}
        >

          {/* Tab bar */}
          <div
            className="content-stretch flex items-center overflow-clip relative shrink-0 w-full"
            style={{ paddingLeft: '24px', paddingRight: '24px' }}
          >
            <div className="content-stretch flex items-center overflow-clip relative shrink-0">
              {/* Description tab — active */}
              <div
                className="border-b-2 border-solid content-stretch flex flex-col items-center justify-center min-h-[48px] min-w-[48px] relative shrink-0"
                style={{ borderColor: textPrimary, gap: '8px', paddingTop: '12px', paddingBottom: '12px' }}
              >
                <div className="content-stretch flex items-center justify-center relative shrink-0" style={{ gap: '8px' }}>
                  <p
                    className="not-italic relative shrink-0 whitespace-nowrap"
                    style={{ fontFamily: fontPoppins, fontWeight: 600, lineHeight: '22px', color: textPrimary, fontSize: '14px' }}
                  >
                    Description
                  </p>
                </div>
              </div>
              {/* Tab spacing */}
              <div className="content-stretch flex flex-col items-center relative shrink-0">
                <div className="shrink-0 size-[24px]" />
                <div className="h-px shrink-0 w-[40px]" />
              </div>
            </div>
            {/* Bottom divider */}
            <div
              className="absolute bottom-0 h-[2px] left-0 right-0"
              style={{ backgroundColor: 'rgba(189,188,201,0.08)' }}
            />
          </div>

          {/* Tab content */}
          <div
            className="content-stretch flex flex-col items-start relative shrink-0 w-full"
            style={{ gap: '40px', padding: '24px' }}
          >

            {/* Specifications section */}
            <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ gap: '16px' }}>
              <p className="not-italic relative shrink-0 w-full" style={typo.h6}>
                Specifications
              </p>
              <div
                className="border border-solid content-stretch flex items-start max-w-[640px] relative shrink-0 w-full"
                style={{ borderColor: 'rgba(189,188,201,0.2)' }}
              >
                {/* Label column */}
                <div
                  className="border-r border-solid content-stretch flex flex-col items-start relative shrink-0 w-2/5 min-w-[120px]"
                  style={{ borderColor: 'rgba(189,188,201,0.2)' }}
                >
                  <div
                    className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                    style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                      Category
                    </p>
                  </div>
                  {p.sku && (
                    <div
                      className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                        Serial number
                      </p>
                    </div>
                  )}
                  {p.internal_code && (
                    <div
                      className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                        Internal code
                      </p>
                    </div>
                  )}
                  <div
                    className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                    style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                      Gender
                    </p>
                  </div>
                  <div
                    className="content-stretch flex items-center relative shrink-0 w-full"
                    style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                      Ships from
                    </p>
                  </div>
                  {p.tags.length > 0 && (
                    <div
                      className="border-t border-solid content-stretch flex items-start relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <p className="not-italic relative shrink-0 w-full" style={typo.body2Secondary}>
                        Tags
                      </p>
                    </div>
                  )}
                </div>
                {/* Value column */}
                <div className="content-stretch flex flex-col items-start relative flex-1 min-w-0">
                  <div
                    className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                    style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative" style={typo.body2}>
                      {CATEGORY_LABELS[p.category]}
                    </p>
                  </div>
                  {p.sku && (
                    <div
                      className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative font-mono text-xs" style={typo.body2}>
                        {p.sku}
                      </p>
                    </div>
                  )}
                  {p.internal_code && (
                    <div
                      className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative font-mono text-xs" style={typo.body2}>
                        {p.internal_code}
                      </p>
                    </div>
                  )}
                  <div
                    className="border-b border-solid content-stretch flex items-center relative shrink-0 w-full"
                    style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative capitalize" style={typo.body2}>
                      {p.gender}
                    </p>
                  </div>
                  <div
                    className="content-stretch flex items-center relative shrink-0 w-full"
                    style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                  >
                    <p className="flex-[1_0_0] min-h-px min-w-px not-italic relative" style={typo.body2}>
                      United States
                    </p>
                  </div>
                  {p.tags.length > 0 && (
                    <div
                      className="border-t border-solid content-stretch flex items-center relative shrink-0 w-full"
                      style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px', paddingBottom: '8px' }}
                    >
                      <div className="flex flex-wrap gap-1 min-w-0 min-h-px">
                        {p.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs rounded-full bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Product details + Benefits */}
            <div className="content-stretch flex flex-col items-start not-italic relative shrink-0 w-full" style={{ gap: '16px' }}>
              {p.content ? (
                <div
                  className="prose prose-sm max-w-none w-full"
                  style={{
                    // Apply typography via CSS cascade for prose children
                  }}
                  dangerouslySetInnerHTML={{ __html: p.content }}
                />
              ) : (
                <>
                  <p className="not-italic relative shrink-0 w-full" style={typo.h6Purple}>
                    Product details
                  </p>
                  <ul className="block leading-[0] list-disc relative shrink-0 w-full" style={{ ...typo.body2, letterSpacing: '0px' }}>
                    <li className="ms-[21px]">
                      <span style={{ lineHeight: '22px' }}>No detailed description provided.</span>
                    </li>
                  </ul>
                </>
              )}
            </div>

            {/* Created date */}
            <div className="flex items-center gap-2 w-full">
              <span style={typo.body2Secondary}>
                Created
              </span>
              <span style={typo.body2}>
                {formatDate(p.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* ── Claims section ─────────────────────────────────────────────────── */}
        <div
          className="content-stretch flex flex-col items-start overflow-clip relative shrink-0 w-full bg-surface-elevated"
          style={{ borderRadius: '16px', boxShadow: '0px 0px 2px 0px rgba(189,188,201,0.2), 0px 12px 24px -4px rgba(189,188,201,0.12)' }}
        >

          {/* Claims header */}
          <div
            className="border-b w-full"
            style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '24px', paddingRight: '24px', paddingTop: '16px', paddingBottom: '16px' }}
          >
            <p className="not-italic" style={typo.subtitle2}>
              Claims
              {claimsPagination && (
                <span className="ml-2" style={typo.body2Secondary}>
                  ({claimsPagination.total})
                </span>
              )}
            </p>
          </div>

          {claims.length === 0 ? (
            <div className="py-10 text-center w-full" style={{ paddingLeft: '24px', paddingRight: '24px' }}>
              <p style={typo.body2Secondary}>
                No claims yet
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto w-full">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(189,188,201,0.08)' }}>
                      <th
                        className="text-left py-3 font-semibold uppercase tracking-wide"
                        style={{ ...typo.caption, paddingLeft: '24px', paddingRight: '24px' }}
                      >
                        Learner
                      </th>
                      <th
                        className="text-left py-3 font-semibold uppercase tracking-wide"
                        style={{ ...typo.caption, paddingLeft: '24px', paddingRight: '24px' }}
                      >
                        Status
                      </th>
                      <th
                        className="text-left py-3 font-semibold uppercase tracking-wide"
                        style={{ ...typo.caption, paddingLeft: '24px', paddingRight: '24px' }}
                      >
                        Points
                      </th>
                      <th
                        className="text-left py-3 font-semibold uppercase tracking-wide"
                        style={{ ...typo.caption, paddingLeft: '24px', paddingRight: '24px' }}
                      >
                        Date
                      </th>
                      <th
                        className="text-right py-3 font-semibold uppercase tracking-wide"
                        style={{ ...typo.caption, paddingLeft: '24px', paddingRight: '24px' }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((claim) => (
                      <tr
                        key={claim.id}
                        className="border-t hover:bg-grey-50/50 dark:hover:bg-grey-800/20 transition-colors"
                        style={{ borderColor: 'rgba(189,188,201,0.2)' }}
                      >
                        <td className="py-3 font-medium" style={{ ...typo.body2, paddingLeft: '24px', paddingRight: '24px' }}>
                          {claim.learner_name ?? (claim.learner_id ?? claim.membership_id)?.slice(0, 8) ?? 'Unknown'}
                        </td>
                        <td className="py-3" style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                          <span
                            className={`inline-block px-2 py-0.5 font-semibold rounded-full capitalize text-[12px] ${
                              CLAIM_STATUS_STYLES[claim.status] ?? ''
                            }`}
                            style={{ fontFamily: fontPoppins }}
                          >
                            {claim.status}
                          </span>
                        </td>
                        <td className="py-3 font-medium" style={{ ...typo.body2Purple, paddingLeft: '24px', paddingRight: '24px' }}>
                          {claim.points_spent.toLocaleString()} pts
                        </td>
                        <td className="py-3" style={{ ...typo.body2Secondary, paddingLeft: '24px', paddingRight: '24px' }}>
                          {formatDate(claim.created_at)}
                        </td>
                        <td className="py-3 text-right" style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                          {claim.status === 'pending' && (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleFulfill(claim)}
                                disabled={fulfillMutation.isPending}
                                className="inline-flex items-center justify-center rounded-full bg-success-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-success-700 dark:bg-success-500 dark:hover:bg-success-400 focus-ring transition-colors active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleCancelClaim(claim)}
                                disabled={cancelMutation.isPending}
                                className="inline-flex items-center justify-center rounded-full border border-error-300 px-4 py-2 text-sm font-semibold text-error-600 hover:bg-error-50 dark:border-error-700 dark:text-error-400 dark:hover:bg-error-950/30 focus-ring transition-colors active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Reject &amp; refund
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {claimsPagination && claimsPagination.pages > 1 && (
                <div
                  className="flex items-center justify-center gap-2 py-4 border-t w-full"
                  style={{ borderColor: 'rgba(189,188,201,0.2)', paddingLeft: '24px', paddingRight: '24px' }}
                >
                  <button
                    onClick={() => setClaimsPage((p) => Math.max(1, p - 1))}
                    disabled={claimsPage <= 1}
                    className="p-1.5 rounded-full text-text-secondary hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-40 transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  <span style={typo.caption}>
                    Page {claimsPage} of {claimsPagination.pages}
                  </span>
                  <button
                    onClick={() => setClaimsPage((p) => Math.min(claimsPagination.pages, p + 1))}
                    disabled={claimsPage >= claimsPagination.pages}
                    className="p-1.5 rounded-lg text-text-secondary hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-40 transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom padding */}
        <div className="h-10 shrink-0 w-full" />
      </div>
      </phantom-ui>
    </motion.div>
  );
}
